import { useState, useMemo, useEffect } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyModal } from '~/components/ui/MyModal'
import Stepper from './Stepper'
import Link from 'next/link'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import {
  getAIMemberListVariables,
  getAIMemberList,
  getAIMemberList_accountModule_userMemberCardList,
  getAITotalInfo_pictureGraph_aiArtworkTotalInfo,
  getAITotalInfo,
  MemberCardRightTypeEnum,
} from '~/graphqls/types'
import { GET_AI_MEMBER_LIST, GET_AI_TOTAL_INFO } from '~/graphqls/gqls'
import { timeWithUint } from '~/utils'
import { useStores } from '~/hooks/useStores'
import { observer } from 'mobx-react'
import { MyLink } from '~/components/ui/MyLink'

interface Props {
  visible: boolean
  queueInfo: {
    queue: number
    expectedSeconds: number
    reduceTime: number
  }
  close: () => void
  buyFn: ({
    points,
    vipTimes,
  }: {
    points: number
    vipTimes: number
  }) => Promise<void>
}

function SpeedCreateModal({ visible, close, queueInfo, buyFn }: Props) {
  const [totalInfo, setTotalInfo] =
    useState<getAITotalInfo_pictureGraph_aiArtworkTotalInfo>()
  const [memberList, setMemberList] = useState<
    getAIMemberList_accountModule_userMemberCardList[]
  >([])
  const [accelarateTimes, setAccelarateTimes] = useState(1)
  const [loading, setLoading] = useState(false)

  const { store } = useStores()

  const getMemberListAction = useImperativeQuery<
    getAIMemberList,
    getAIMemberListVariables
  >(GET_AI_MEMBER_LIST)
  const getTotalInfoAction =
    useImperativeQuery<getAITotalInfo>(GET_AI_TOTAL_INFO)

  const fetchData = () => {
    getTotalInfoAction()
      .then(res => {
        setTotalInfo(
          (res.data.pictureGraph
            ?.aiArtworkTotalInfo as getAITotalInfo_pictureGraph_aiArtworkTotalInfo) ||
            {}
        )
      })
      .catch(err => {
        console.error(err)
      })
    getMemberListAction()
      .then(res => {
        setMemberList(res.data.accountModule?.userMemberCardList || [])
      })
      .catch(err => {
        console.error(err)
      })
  }

  useEffect(() => {
    fetchData()
  }, [])

  /** 权益卡可用加速次数 */
  let memberAccelerateTimes = 0
  memberList.forEach(item => {
    item.holdCardInfo?.forEach(i => {
      if (i?.rightType === MemberCardRightTypeEnum.ACCELERATE) {
        memberAccelerateTimes += i.num || 0
      }
    })
  })

  /** 总积分 */
  const availablePoints = totalInfo?.userIntegral

  /** 消耗积分 */
  const consumedPoints = Math.max(
    (accelarateTimes - memberAccelerateTimes) * 2,
    0
  )

  const handleBuy = async () => {
    setLoading(true)
    await buyFn({
      points: consumedPoints,
      vipTimes: Math.min(memberAccelerateTimes, accelarateTimes),
    })
    store.ai?.setIsRefreshMemberList(true)
    store.ai?.setIsRefreshTotalInfo(true)
    fetchData()
    setLoading(false)
  }

  const maxPoint = useMemo(() => {
    return queueInfo.queue - 1
  }, [queueInfo])

  return (
    <MyModal
      open={visible}
      title="加速生成"
      onCancel={close}
      maskStyle={{
        backgroundColor: 'rgba(0,0,0,0.8)',
      }}
      width={375}
      footer={null}
      centered
      dark
    >
      <div className="flex items-center justify-between">
        <span className="text-sm text-neutral-500 font-medium">加速次数</span>
        <Stepper
          step={1}
          value={accelarateTimes}
          min={1}
          max={maxPoint}
          onChange={setAccelarateTimes}
        />
      </div>
      <div className="w-full h-px bg-neutral-200 my-3"></div>
      {!!memberAccelerateTimes && (
        <div className="flex justify-between items-center">
          <div className="text-sm font-medium text-neutral-500">
            剩余权益卡加速次数
          </div>
          <div className="text-neutral-800 font-medium text-sm">
            {memberAccelerateTimes} 次
          </div>
        </div>
      )}

      {!!consumedPoints && (
        <>
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium text-neutral-500">消耗积分</div>
            <div className="text-error font-medium text-sm">
              {consumedPoints} 积分
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm font-medium text-neutral-500">可用积分</div>
            <div className="text-neutral-800 font-medium text-sm">
              {availablePoints} 积分
            </div>
          </div>
        </>
      )}

      <div className="flex justify-between items-center mt-3">
        <div className="text-sm font-medium text-neutral-500">预计排队</div>
        <div className="text-neutral-800 font-medium text-sm">
          {timeWithUint(
            queueInfo.expectedSeconds +
              queueInfo.reduceTime -
              accelarateTimes * queueInfo?.reduceTime
          )}
        </div>
      </div>
      <div className="bg-neutral-200 text-neutral-500 text-center text-xs py-2 rounded-2xl mt-3">
        积分不足？可前往{' '}
        <MyLink href="/integral" className="text-primary" exist={true}>
          「我的积分」
        </MyLink>{' '}
        页面充值积分
      </div>
      <MyBtn
        type="primary"
        className="w-full mt-4"
        onClick={handleBuy}
        loading={loading}
        disabled={!accelarateTimes}
        $dark={true}
      >
        确定
      </MyBtn>
    </MyModal>
  )
}

export default observer(SpeedCreateModal)
